import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  checkPublishedShiftPlanV2Enabled,
  setPublishedShiftPlanV2Enabled,
} from '../../../store/slices/v2/publishedShiftPlanFeatureSlice';
import { isLocalEnvironment } from '../../../utils/environmentDetection';
import CurrentPlanSummaryContainer from './CurrentPlanSummaryContainer';
import CurrentPlanSummaryV2Container from './v2/CurrentPlanSummaryContainer';
import { SPMSGetShiftPlanResponse } from '../../../types/shiftPlan';

/**
 * Feature gate for the refactored (V2) published shift plan page (UTR-REFLEX-1163).
 *
 * Resolves the `RCC_PUBLISHED_SHIFT_PLAN_V2` flag (RnRRCCFacadeService IsFeatureEnabled)
 * and renders V2 when enabled, else V1. While unresolved (`isV2Enabled === null`) it
 * renders nothing — mounting V1 first and swapping to V2 on resolve would remount at
 * the same slot and fetch twice, so we wait and mount the right page once. The
 * unresolved window is a facade round-trip when deployed, instant locally.
 *
 * Follows the established checkout gate pattern (checkoutSlice + ShiftPlanLayout).
 */
interface CurrentPlanSummaryGatedProps {
  shiftPlans: SPMSGetShiftPlanResponse[];
  selectedTab: string;
  selectedDate: string;
  selectedShift: string;
  onNeedsTakeOver: (displayMessage: string) => void;
  onPlanSuccess: (planId: string) => void;
}

const CurrentPlanSummaryGated: React.FC<CurrentPlanSummaryGatedProps> = (props) => {
  const dispatch = useAppDispatch();
  const isV2Enabled = useAppSelector(
    (state) => state.publishedShiftPlanFeature.isPublishedShiftPlanV2Enabled
  );

  useEffect(() => {
    if (isV2Enabled !== null) {
      return;
    }
    // Local dev: default to V1 (false) so in-progress V2 work doesn't disrupt
    // anyone building on the existing page. To preview V2 locally, flip this to
    // true while developing. (getUserName() can't resolve without a Boson parent
    // frame, so we don't call the facade locally.)
    if (isLocalEnvironment()) {
      dispatch(setPublishedShiftPlanV2Enabled(false));
      return;
    }
    // All deployed environments: let the facade AppConfig allowlist decide.
    dispatch(checkPublishedShiftPlanV2Enabled());
  }, [dispatch, isV2Enabled]);

  // Flag not resolved yet — render nothing so we don't mount a page we'd only
  // have to unmount once the flag lands (see the note above on avoiding remounts).
  if (isV2Enabled === null) {
    return null;
  }

  if (isV2Enabled) {
    return <CurrentPlanSummaryV2Container {...props} />;
  }

  // Flag resolved false → existing V1 page.
  return <CurrentPlanSummaryContainer {...props} />;
};

export default CurrentPlanSummaryGated;
